import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { PROPOSAL_BUNDLE_CATEGORY, type ProposalBundle } from '../../types/proposalBundles';
import PairRoundTripCard from './PairRoundTripCard';
import SingleBundleCard from './SingleBundleCard';

interface Props {
  bundles: ProposalBundle[];
  includeTax?: boolean;
}

type RoundTripPair = {
  outwardBundle: ProposalBundle;
  homewardBundle: ProposalBundle;
};

type LatestProposalDisplay = {
  roundTripPair: RoundTripPair | null;
  singleDisplayBundles: ProposalBundle[];
};

const LatestProposals = ({ bundles, includeTax = false }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const { roundTripPair, singleDisplayBundles } = useMemo(() => buildLatestProposalDisplay(bundles), [bundles]);

  if (bundles.length === 0) {
    return <p style={styles.placeholder}>提案内容がまだありません</p>;
  }

  return (
    <div style={styles.container}>
      {roundTripPair ? (
        <PairRoundTripCard
          outward={roundTripPair.outwardBundle}
          homeward={roundTripPair.homewardBundle}
          includeTax={includeTax}
        />
      ) : null}
      {singleDisplayBundles.map(bundle => (
        <SingleBundleCard key={bundle.id} bundle={bundle} includeTax={includeTax} />
      ))}
    </div>
  );
};

const findRoundTripLegBundles = (bundles: ProposalBundle[]) => {
  const outwardBundle = bundles.find(bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD);
  const homewardBundle = bundles.find(bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD);
  return { outwardBundle, homewardBundle };
};

const filterNonRoundTripLegBundles = (bundles: ProposalBundle[]): ProposalBundle[] =>
  bundles.filter(
    bundle =>
      bundle.category !== PROPOSAL_BUNDLE_CATEGORY.OUTWARD && bundle.category !== PROPOSAL_BUNDLE_CATEGORY.HOMEWARD
  );

const buildSingleDisplayBundles = (
  outwardBundle: ProposalBundle | undefined,
  homewardBundle: ProposalBundle | undefined,
  nonRoundTripLegBundles: ProposalBundle[]
): ProposalBundle[] => [
  ...(outwardBundle != null ? [outwardBundle] : []),
  ...(homewardBundle != null ? [homewardBundle] : []),
  ...nonRoundTripLegBundles
];

const buildLatestProposalDisplay = (bundles: ProposalBundle[]): LatestProposalDisplay => {
  const { outwardBundle, homewardBundle } = findRoundTripLegBundles(bundles);
  const nonRoundTripLegBundles = filterNonRoundTripLegBundles(bundles);

  if (outwardBundle == null || homewardBundle == null) {
    return {
      roundTripPair: null,
      singleDisplayBundles: buildSingleDisplayBundles(outwardBundle, homewardBundle, nonRoundTripLegBundles)
    };
  }

  return {
    roundTripPair: {
      outwardBundle,
      homewardBundle
    },
    singleDisplayBundles: nonRoundTripLegBundles
  };
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginTop: '12px'
  },
  placeholder: {
    textAlign: 'center' as const,
    color: theme.grayTextColor,
    margin: '16px 0 0',
    fontSize: '13px'
  }
});

export default LatestProposals;
